import { el, mount, onTap } from './dom.ts';
import { t } from '../i18n/index.ts';

// Durum ekranları kutu içinde dev ikonlu panolar değil; sola hizalı, kısa ve
// eyleme çıkaran metinler. Boş ekran bir davettir, hata ne olduğunu söyler.

// Oynatıcı açıkken bildirim ve iletişim kutusu ONUN içine eklenir. Oynatıcı
// sayfanın üstünde ayrı bir katman (ve tam ekranda tek görünen öğe): gövdeye
// eklenen bir kutu arkasında kalıyor, izleyici "Bağlanıyor…" perdesine bakarken
// onay bekleyen soruyu göremiyordu.
const playerLayer = () => document.querySelector<HTMLElement>('.player');

function stack(): HTMLElement {
  const player = playerLayer();
  if (!player) return document.getElementById('toasts')!;
  let inner = player.querySelector<HTMLElement>(':scope > .toasts');
  if (!inner) {
    inner = el('div', { class: 'toasts' });
    player.appendChild(inner);
  }
  return inner;
}

export function toast(message: string, kind: 'info' | 'error' | 'ok' = 'info', ms = 4200): void {
  const node = el('div', { class: `toast ${kind}`, role: 'status' }, message);
  stack().appendChild(node);
  setTimeout(() => node.remove(), ms);
}

export interface StateOptions {
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  tone?: 'empty' | 'error';
}

export function stateBlock(opts: StateOptions): HTMLElement {
  const actions = el('div', { class: 'row-actions' });
  if (opts.action) {
    const primary = el('button', { class: 'btn btn-light', type: 'button' }, opts.action.label);
    onTap(primary, opts.action.onClick);
    actions.appendChild(primary);
  }
  if (opts.secondary) {
    const second = el('button', { class: 'btn btn-quiet', type: 'button' }, opts.secondary.label);
    onTap(second, opts.secondary.onClick);
    actions.appendChild(second);
  }
  return el('div', { class: 'state' },
    el('h3', {}, opts.title),
    opts.body ? el('p', {}, opts.body) : null,
    opts.action || opts.secondary ? actions : null,
  );
}

export function errorNote(message: string): HTMLElement {
  return el('p', { class: 'error-note', role: 'alert' }, message);
}

export function okNote(message: string): HTMLElement {
  return el('p', { class: 'ok-note' }, message);
}

export function skeletons(count = 8, poster = false): HTMLElement {
  const wrap = el('div', { class: 'skeleton-grid' + (poster ? ' posters' : ''), role: 'status', 'aria-label': t('common.loading') });
  for (let i = 0; i < count; i++) {
    wrap.appendChild(el('div', { 'aria-hidden': 'true' },
      el('div', { class: 'sk' }),
      el('div', { class: 'sk line' }),
      el('div', { class: 'sk line short' }),
    ));
  }
  return wrap;
}

/** Reserve the same stage as the real feature, including its copy and action. */
export function skeletonHero(): HTMLElement {
  return el('div', { class: 'app-hero hero-loading', role: 'status', 'aria-label': t('common.loading') },
    el('div', { class: 'scene' },
      el('div', { class: 'hero-loading-art sk', 'aria-hidden': 'true' }),
      el('div', { class: 'scene-copy' },
        el('span', { class: 'tag' }, t('discover.loading')),
        el('div', { class: 'sk hero-loading-title', 'aria-hidden': 'true' }),
        el('div', { class: 'sk hero-loading-title short', 'aria-hidden': 'true' }),
        el('div', { class: 'scene-actions', 'aria-hidden': 'true' }, el('div', { class: 'sk hero-loading-action' })),
      ),
    ),
  );
}

export interface ConfirmOptions {
  title: string;
  body?: string | Node;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const confirmBtn = el('button', {
      class: `btn ${options.danger ? 'btn-danger' : 'btn-light'}`, type: 'button',
    }, options.confirmLabel ?? t('feedback.confirm'));
    const cancelBtn = el('button', { class: 'btn btn-quiet', type: 'button' }, options.cancelLabel ?? t('feedback.cancel'));

    const titleId = 'dlg_' + Math.random().toString(36).slice(2, 9);
    const opener = document.activeElement as HTMLElement | null;
    const backdrop = el('div', { class: 'backdrop', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId },
      el('div', { class: 'modal' },
        el('h2', { id: titleId }, options.title),
        typeof options.body === 'string' ? el('p', {}, options.body) : options.body ?? null,
        el('div', { class: 'modal-actions' }, cancelBtn, confirmBtn),
      ),
    );

    const close = (value: boolean) => {
      document.removeEventListener('keydown', onKey);
      backdrop.remove();
      try { opener?.focus(); } catch { /* öğe artık yok */ }
      resolve(value);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };

    onTap(confirmBtn, () => close(true));
    onTap(cancelBtn, () => close(false));
    backdrop.addEventListener('pointerdown', (e) => { if (e.target === backdrop) close(false); });
    document.addEventListener('keydown', onKey);
    (playerLayer() ?? document.body).appendChild(backdrop);
    // Gövdede doldurulacak bir alan varsa (parola) odak oraya; yoksa onay düğmesine.
    const firstField = backdrop.querySelector<HTMLElement>('input, select, textarea');
    (firstField ?? confirmBtn).focus();
  });
}

/** Ayarlar/paketler gibi satır tabanlı ekranlar için iskelet: gelecek
 *  içerikle aynı yükseklikte sessiz bloklar; metin yerine. */
export function skeletonRows(count = 3): HTMLElement {
  const wrap = el('div', { class: 'skeleton-rows' });
  for (let i = 0; i < count; i++) {
    wrap.appendChild(el('div', { class: 'sk-row' },
      el('div', { class: 'sk line' }), el('div', { class: 'sk line short' })));
  }
  return wrap;
}

/** Paket kartlarının iskeleti: üç kart, aynı ölçüde. */
export function skeletonPlans(): HTMLElement {
  const card = () => el('div', { class: 'sk-card' },
    el('div', { class: 'sk line short' }), el('div', { class: 'sk line' }),
    el('div', { class: 'sk amount' }), el('div', { class: 'sk line' }),
    el('div', { class: 'sk line' }), el('div', { class: 'sk line short' }));
  return el('div', { class: 'skeleton-plans' }, card(), card(), card());
}

export function loadingInto(container: Element, count = 8, poster = false): void {
  mount(container, skeletons(count, poster));
}
